<script lang="ts" setup>
import { useEditUser } from '@ilteducation/customer-mgmt';
import { SelfConfirmTextField } from '@ilteducation/text-field/self-confirmed-text-field';
import PermissionLock from '../../../PermissionLock.vue';
import { useLocale } from '../../../../composables/useLocale';
import { createErrorParser } from '../../../../error-utils';
import { ref } from 'vue';
import { useValidator } from '../useValidator';
import { computed } from 'vue';
import { useUserIdentifiers } from '@ilteducation/user-identifiers';

type Props = {
  type: string;
  loading?: boolean;
  disabled?: boolean;
  userUid: string;
  forbiddenError?: string | boolean;
};

type Emits = {
  (e: 'updated'): void;
};

const { t } = useLocale();
const { updateUser, isLoading, error } = useEditUser();
const { mailLike, swedishPhoneNumber, bankId, other } = useValidator();
const { getIdentifier } = useUserIdentifiers();

const props = defineProps<Props>();
const model = defineModel<string>();
const emits = defineEmits<Emits>();

const requestErrorMessage = ref<string>();
const waitingForDeleteApproval = ref<boolean>(false);

const identifier = computed(() => getIdentifier(props.type));

const rules = computed(() => {
  if (waitingForDeleteApproval.value) {
    return [];
  }
  if (!identifier.value) {
    return other();
  }
  switch (identifier.value.format) {
    case 'email':
      return mailLike();
    case 'phone':
      return swedishPhoneNumber(t);
    case 'pnr':
      return bankId();
    case 'text':
    default:
      return other();
  }
});

const label = computed(() => identifier.value?.name || props.type.charAt(0).toUpperCase() + props.type.slice(1));

const placeholder = computed(() => {
  if (!identifier.value) {
    return t('lcp.text_identifier_example');
  }
  switch (identifier.value.format) {
    case 'email':
      return t('lcp.email_example');
    case 'phone':
      return '+46701234567';
    case 'pnr':
      return 'YYYYMMDDXXXX';
    case 'text':
    default:
      return t('lcp.text_identifier_example');
  }
});

const icon = computed(() => {
  return identifier.value?.iconDataUri || 'mdi-window-restore';
});

const saveIdentifier = async (newValue: string) => {
  requestErrorMessage.value = undefined;
  let successful: boolean;
  if (waitingForDeleteApproval.value) {
    // delete
    successful = await updateUser(props.userUid, {
      identifiers: { [props.type]: null },
    });
  } else {
    successful = await updateUser(props.userUid, {
      identifiers: { [props.type]: newValue },
    });
  }

  if (!successful) {
    requestErrorMessage.value = createErrorParser(t)(error.value);
  } else {
    emits('updated');
  }

  return successful;
};

const onReject = () => {
  requestErrorMessage.value = undefined;
  waitingForDeleteApproval.value = false;
  return true;
};

const onDeleteIdentifierClicked = () => {
  waitingForDeleteApproval.value = true;
};

const changedValue = ref<string | undefined>(model.value);
const onValueUpdate = (newValue: string) => {
  changedValue.value = newValue;
};
</script>

<template>
  <v-row>
    <v-col>
      <SelfConfirmTextField
        :model-value="model"
        :prepend-inner-icon="icon"
        :label="label"
        :placeholder="placeholder"
        :loading="loading || isLoading"
        :disabled="disabled || !!forbiddenError"
        :onApprove="saveIdentifier"
        :onReject="onReject"
        :show-confirm="waitingForDeleteApproval"
        :onUpdate="onValueUpdate"
        :rules="rules"
        :color="requestErrorMessage ? 'error' : undefined"
        :messages="requestErrorMessage"
      >
        <template v-if="requestErrorMessage" v-slot:message>
          <!--
      Why not error-message but force message to looks like error?
      SelfComfirmedText has a comfirmation button gets disabled if validation fails. Veutify validation accepts "error-messages" as validation error.
      Therefore users lose oppurtunity to try again on an error.
       -->
          <div class="text-error text-caption">{{ requestErrorMessage }}</div>
        </template>
      </SelfConfirmTextField>
    </v-col>
    <v-col cols="auto" class="d-flex align-center justify-end pl-0">
      <v-btn
        v-if="!forbiddenError"
        icon="mdi-delete"
        variant="text"
        :disabled="changedValue !== model"
        @click="onDeleteIdentifierClicked"
      />
      <PermissionLock v-else :reason="forbiddenError || undefined" />
    </v-col>
  </v-row>
</template>
